import { Injectable, effect, inject, signal } from '@angular/core';
import { catchError, map, of, take, throwError } from 'rxjs';
import { AuthService } from '@/app/core/services/auth.service';
import { BaseService, SHOULD_SHOW_ERROR_TOAST, SHOULD_SHOW_SUCCESS_TOAST } from '@/app/core/services/base.service';
import { LoadingService } from '@/app/core/services/loading.service';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '@/app/services/toast.service';
import { LanguageService } from '@/app/core/services/language.service';
import { API_VERSION_ENUM, MICROSERVICE_ENUM, VEHICLE_STATUS_ENDPOINT } from '@/app/consts/api.const';
import { RoleEnum } from '@/app/consts/role.const';

type VehicleStatusBody = { vinNumber: string; generalAttribute: string; tag2?: string };
export interface CheckInRecord {
  role: string;
  vehicle: VehicleStatusBody & { id: string };
  checkedInAt: string;
}

const getCheckInEndpoint = (vehicleId: string) => `${VEHICLE_STATUS_ENDPOINT}/${vehicleId}/checkin`;
const getCheckOutEndpoint = (vehicleId: string) => `${VEHICLE_STATUS_ENDPOINT}/${vehicleId}/checkout`;

@Injectable({
  providedIn: 'root'
})
export class QuickActionStateService {
  private readonly authService = inject(AuthService);
  private readonly baseService = inject(BaseService);
  private readonly loadingService = inject(LoadingService);
  private readonly languageService = inject(LanguageService);
  private readonly _checkInState = signal<CheckInRecord | null>(null);
  private readonly toastService = inject(ToastService);
  readonly checkInState = this._checkInState.asReadonly();
  private readonly suppressToastContext = new HttpContext()
    .set(SHOULD_SHOW_SUCCESS_TOAST, false)
    .set(SHOULD_SHOW_ERROR_TOAST, false);

  constructor() {
    effect(() => {
      const selectedRole = this.authService.selectedRole();
      const currentRecord = this._checkInState();
      if (currentRecord && currentRecord.role !== selectedRole) {
        this._checkInState.set(null);
      }
    });

    effect(() => {
      const selectedRole = this.authService.selectedRole();
      if (selectedRole === RoleEnum.Driver) {
        this.getCheckStatus(selectedRole);
      }
    });
  }

  private getCheckStatus(activeRole: string) {

    const customErrorHandler = (error: HttpErrorResponse) => {
      const { message, parameters } = error.error || {};
      if(error.status === 404) {
       this.toastService.showWarning(this.languageService.instant('toast.error'), '', this.languageService.instant('checkin.vehicleUnavailable'));
      }
      return throwError(() => new Error(error.message || 'Server error'));
    }
    this.baseService.getV3<VehicleStatusBody>(
        VEHICLE_STATUS_ENDPOINT + '/own',
        undefined,
        undefined,
        this.suppressToastContext,
        customErrorHandler,
        MICROSERVICE_ENUM.status,
        API_VERSION_ENUM.v3
      )
      .pipe(
        take(1),
        map((res) => {
          if (!res) {
            this._checkInState.set(null);
            return;
          }
          this._checkInState.set({
            role: activeRole,
            checkedInAt: new Date().toISOString(),
            vehicle: {
              vinNumber: res.vinNumber,
              generalAttribute: res.generalAttribute,
              id: (res as any).vehicleId,
              tag2: res.tag2 // tag2 is used to determine if the vehicle is BEV or not, which affects quick action visibility
            },
          });
        })
      )
      .subscribe({
        error: () => {
          this._checkInState.set(null);
        },
      });
  }

  requestCheckIn(vehicleId: string, tag2: string = 'ICE', vinNumber: string, generalAttribute?: string) {
    const role = this.authService.selectedRole();
    if (!role) {
      return of(false);
    }

    const context = new HttpContext();
    context.set(SHOULD_SHOW_SUCCESS_TOAST, false);
    context.set(SHOULD_SHOW_ERROR_TOAST, false);

    const customErrorHandler = (error: HttpErrorResponse) => {
      const { message, parameters } = error.error || {};
      if(error.status === 400) {
        if(message === 'VehicleHasCheckedIn') {
          this.toastService.showWarning(this.languageService.instant('toast.error'), '', this.languageService.instant('checkin.vehicleAlreadyCheckedIn', { user: parameters?.[0] || this.languageService.instant('common.theUser') }));
        } else if(message === 'DriverHasCheckedIn') {
          this.toastService.showWarning(this.languageService.instant('toast.error'), '', this.languageService.instant('checkin.driverAlreadyCheckedIn'));
        }
      }
      return throwError(() => new Error(error.message || 'Server error'));
    }
    const request$ = this.baseService.putV3<any>(getCheckInEndpoint(vehicleId), undefined, undefined, context, customErrorHandler, MICROSERVICE_ENUM.status,API_VERSION_ENUM.v3);
    return this.loadingService.wrapWithLoading(request$, 'Checking in...', 'updating').pipe(
      map((res) => {
        if(!res) {
            this._checkInState.set({
              role,
              checkedInAt: new Date().toISOString(),
              vehicle: {
                vinNumber, 
                id: vehicleId, 
                generalAttribute: generalAttribute || vinNumber,
                tag2: tag2 // tag2 is used to determine if the vehicle is BEV or not, which affects quick action visibility
              },
            });
          return true; 
        } else {
          return false;
        }
      }),
      catchError(() => of(false))
    );
  }

  requestCheckOut(vehicleId: string) {
    const role = this.authService.selectedRole();
    if (!role) {
      return of(false);
    }

    const request$ = this.baseService.putV3<void>(getCheckOutEndpoint(vehicleId), undefined, undefined, this.suppressToastContext, undefined,MICROSERVICE_ENUM.status,API_VERSION_ENUM.v3 );
    return this.loadingService.wrapWithLoading(request$, 'Checking out...', 'updating').pipe(
      map(() => {
        this.clearCheckIn();
        return true;
      }),
      catchError(() => of(false))
    );
  }

  clearCheckIn(): void {
    this._checkInState.set(null);
  }
}
